import React, { FC, useEffect, useMemo, useState } from 'react';
import { CargoManagementQuery, OmnichannelQuery } from '../../../../services/query';
import Swal from 'sweetalert2';
import { Autocomplete, Box, FormControl, Grid, LinearProgress, TextField } from '@mui/material';
import { AddressEntityType, AddressEntryTypeId } from '../../../../types/enums/addressEntityType-enum';
import { DeliveryMethodEnum } from '../../../../types/enums';
import { useTranslation } from 'react-i18next';
import { updateCustomerAddressRequest } from '../../CustomerManagement.helper';
import {
    CAIP,
    caipCls,
    caipEntityAutocomplete,
    caipEntityFieldModifier,
    caipEntityTextfield,
} from './customerAddressInformationPaper.classes';

interface ICustomerAddressEntityFieldsV2Props {
    customerAddress: updateCustomerAddressRequest | any;
    setCustomerAddress: React.Dispatch<React.SetStateAction<any>>;
    addressRules?: any[];
    deliveryMethodId?: number | null;
}

enum CustomerAddressFields {
    PostalCode = 'postalCodeId',
    Region = 'countryZoneId',
    Neighborhood = 'neighborhoodId',
    County = 'countyId',
    City = 'cityId',
    Street = 'street',
    House = 'house',
    District = 'districtId',
    Flat = 'flat',
    PersonalNumber = 'personalNumber',
}

const getFieldType = (rule: any): number => rule?.field?.fieldType;
const getEntryTypeId = (rule: any): number => rule?.field?.fieldEntryType;

export const CustomerAddressEntityFieldsV2: FC<ICustomerAddressEntityFieldsV2Props> = ({
    customerAddress,
    setCustomerAddress,
    addressRules,
    deliveryMethodId,
}) => {
    const countryId_KZ = 10;
    const countryId_UA = 24;
    const { t } = useTranslation();
    const [addressEntityTypeData, setAddressEntityTypeData] = useState<Record<number, any>>({});
    const countryId = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId;
    const [isAllDataLoaded, setIsAllDataLoaded] = useState(false);
    const [deliveryLocationOptions, setDeliveryLocationOptions] = useState<any[]>([]);
    const [deliveryLocationLoading, setDeliveryLocationLoading] = useState(false);
    const [selectedDeliveryLocation, setSelectedDeliveryLocation] = useState<any>(null);

    const sortedAddressRules = useMemo(() => {
        if (!addressRules?.length) return [];
        return [...addressRules].sort((a, b) => {
            if (a.parentFieldType == null && b.parentFieldType != null) return -1;
            if (a.parentFieldType != null && b.parentFieldType == null) return 1;
            return (getFieldType(a) ?? 0) - (getFieldType(b) ?? 0);
        });
    }, [addressRules]);

    const selectionRulesCount = useMemo(() => {
        return sortedAddressRules.filter((rule: any) => {
            const entryType = AddressEntryTypeId[getEntryTypeId(rule)];
            return entryType === 'Selection' || entryType === 'AllInOneSelection';
        }).length;
    }, [sortedAddressRules]);

    const lastSelectionRule = useMemo(() => {
        const selectionRules = sortedAddressRules.filter((rule: any) => {
            const entryType = AddressEntryTypeId[getEntryTypeId(rule)];
            return entryType === 'Selection' || entryType === 'AllInOneSelection';
        });
        return selectionRules.length > 0 ? selectionRules[selectionRules.length - 1] : null;
    }, [sortedAddressRules]);

    const lastSelectionAddressEntityId = useMemo(() => {
        if (!lastSelectionRule) return null;
        const fieldType = getFieldType(lastSelectionRule);
        const entryType = AddressEntryTypeId[getEntryTypeId(lastSelectionRule)];
        switch (fieldType) {
            case AddressEntityType.PostalCode:
                return entryType === 'Text'
                    ? (customerAddress as any)?.postalCode
                    : (customerAddress as any)?.postalCodeId;
            case AddressEntityType.Region:
                return customerAddress?.countryZoneId || null;
            case AddressEntityType.City:
                return customerAddress?.cityId || null;
            case AddressEntityType.County:
                return customerAddress?.countyId || null;
            case AddressEntityType.Neighborhood:
                return customerAddress?.neighborhoodId || null;
            case AddressEntityType.Street:
                return entryType === 'Text' ? (customerAddress as any)?.streetName : (customerAddress as any)?.streetId;
            case AddressEntityType.District:
                return customerAddress?.districtId || null;
            default:
                return null;
        }
    }, [lastSelectionRule, customerAddress]);

    const showDeliveryLocationSelect =
        Number(deliveryMethodId) === DeliveryMethodEnum.PickPoint ||
        Number(deliveryMethodId) === DeliveryMethodEnum.StorePickup;

    const getLabel = (rule: any): string => {
        const entityTypeName = AddressEntityType[getFieldType(rule)];
        if (entityTypeName) {
            return t(`addressDetail.${entityTypeName}`);
        }
        const titleKey = rule?.field?.fieldLocalize?.titleLocalizeKey;
        if (titleKey) {
            const capitalized = titleKey.charAt(0).toUpperCase() + titleKey.slice(1);
            return t(`addressDetail.${capitalized}`);
        }
        return '';
    };

    const GetPostalCodeList = async () => {
        try {
            CargoManagementQuery.GetPostalCodeList().then((res: any) => {
                const parseData = res.data.addressEntities?.map((item: any) => ({
                    value: item.addressEntityId,
                    text: item.name,
                    parentAddressEntityId: item.parentAddressEntityId,
                }));
                setAddressEntityTypeData((prevData) => ({
                    ...prevData,
                    [AddressEntityType.PostalCode]: parseData,
                }));
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetRegion = async (value?: any) => {
        try {
            CargoManagementQuery.GetRegion({
                parentAddressEntityId: value ? [value] : null,
            }).then((res: any) => {
                const parseData = res.data.addressEntities.map((item: any) => ({
                    value: item.addressEntityId,
                    text: item.name,
                    parentAddressEntityId: item.parentAddressEntityId,
                }));
                setAddressEntityTypeData((prevData) => ({
                    ...prevData,
                    [AddressEntityType.Region]: parseData,
                }));
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetDistrict = async (value?: any) => {
        try {
            CargoManagementQuery.GetDistrict({
                parentAddressEntityId: value ? [value] : null,
            }).then((res: any) => {
                const parseData = res.data.addressEntities.map((item: any) => ({
                    value: item.addressEntityId,
                    text: item.name,
                    parentAddressEntityId: item.parentAddressEntityId,
                }));
                setAddressEntityTypeData((prevData) => ({
                    ...prevData,
                    [AddressEntityType.District]: parseData,
                }));
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetStreet = async (value?: any) => {
        try {
            CargoManagementQuery.GetStreet({
                parentAddressEntityId: value
                    ? [value]
                    : customerAddress.cityId !== null
                      ? [customerAddress.cityId]
                      : [null],
            }).then((res: any) => {
                const parseData = res.data.addressEntities.map((item: any) => ({
                    value: item.addressEntityId,
                    text: item.name,
                    parentAddressEntityId: item.parentAddressEntityId,
                }));
                setAddressEntityTypeData((prevData) => ({
                    ...prevData,
                    [AddressEntityType.Street]: parseData,
                }));
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetCityList = async (value?: any) => {
        try {
            let parentAddressEntityId;

            if (value) {
                parentAddressEntityId = [value];
            } else if (countryId === countryId_KZ || countryId === countryId_UA) {
                parentAddressEntityId = customerAddress.districtId ? [customerAddress.districtId] : [null];
            } else {
                parentAddressEntityId =
                    customerAddress.countryZoneId !== null ? [customerAddress.countryZoneId] : [null];
            }
            CargoManagementQuery.CityList({
                addressEntityTypeId: AddressEntityType.City,
                countryId,
                parentAddressEntityId: parentAddressEntityId,
            }).then((res: any) => {
                if (res.data?.addressEntities) {
                    const parseData = res.data.addressEntities?.map((item: any) => ({
                        value: item.addressEntityId,
                        text: item.name,
                        parentAddressEntityId: item.parentAddressEntityId,
                    }));
                    setAddressEntityTypeData((prevData) => ({
                        ...prevData,
                        [AddressEntityType.City]: parseData,
                    }));
                }
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetCountyList = async (value?: any) => {
        try {
            CargoManagementQuery.CountyList({
                parentAddressEntityIdList: value ? [value] : [customerAddress?.cityId],
                countryId,
            }).then((res: any) => {
                if (res.data?.addressEntities) {
                    const parseData = res.data.addressEntities?.map((item: any) => ({
                        value: item.addressEntityId,
                        text: item.name,
                    }));
                    setAddressEntityTypeData((prevData) => ({
                        ...prevData,
                        [AddressEntityType.County]: parseData,
                    }));
                }
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetSubCountyList = async (value?: any) => {
        try {
            const params = {
                parentAddressEntityId: value ? value : customerAddress?.countyId,
                countryId,
            };
            if (customerAddress?.countyId !== 0) {
                CargoManagementQuery.SubAddressEntities(params).then((res: any) => {
                    if (res.data.addressEntities) {
                        const parseData = res.data.addressEntities?.map((item: any) => ({
                            value: item.addressEntityId,
                            text: item.name,
                        }));
                        setAddressEntityTypeData((prevData) => ({
                            ...prevData,
                            [AddressEntityType.Neighborhood]: parseData,
                        }));
                    }
                });
            }
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    function getValueForEntityType(entityTypeId: number, entryType?: string): string | undefined {
        switch (entityTypeId) {
            case AddressEntityType.PostalCode:
                return entryType === 'Text'
                    ? (customerAddress as any)?.postalCode
                    : (customerAddress as any)?.postalCodeId;
            case AddressEntityType.Region:
                return customerAddress?.countryZoneId;
            case AddressEntityType.City:
                return customerAddress?.cityId;
            case AddressEntityType.County:
                return customerAddress?.countyId;
            case AddressEntityType.Neighborhood:
                return customerAddress?.neighborhoodId;
            case AddressEntityType.Street:
                return entryType === 'Text' ? (customerAddress as any)?.streetName : (customerAddress as any)?.streetId;
            case AddressEntityType.House:
                return customerAddress?.house;
            case AddressEntityType.District:
                return customerAddress?.districtId;
            case AddressEntityType.Flat:
                return customerAddress?.flat;
            case AddressEntityType.PersonalNumber:
                return customerAddress?.personalNumber;
            default:
                return undefined;
        }
    }

    const getFieldForEntityType = (entityTypeId: number): string => {
        switch (entityTypeId) {
            case AddressEntityType.PostalCode:
                return CustomerAddressFields.PostalCode;
            case AddressEntityType.Region:
                return CustomerAddressFields.Region;
            case AddressEntityType.City:
                return CustomerAddressFields.City;
            case AddressEntityType.County:
                return CustomerAddressFields.County;
            case AddressEntityType.Neighborhood:
                return CustomerAddressFields.Neighborhood;
            case AddressEntityType.House:
                return CustomerAddressFields.House;
            case AddressEntityType.District:
                return CustomerAddressFields.District;
            case AddressEntityType.Flat:
                return CustomerAddressFields.Flat;
            case AddressEntityType.PersonalNumber:
                return CustomerAddressFields.PersonalNumber;
            case AddressEntityType.Street:
                return CustomerAddressFields.Street;
            default:
                return '';
        }
    };

    const getFieldForEntityTypeWithEntry = (entityTypeId: number, entryType?: string): string => {
        if (entityTypeId === AddressEntityType.PostalCode) {
            return entryType === 'Text' ? 'postalCode' : 'postalCodeId';
        }
        if (entityTypeId === AddressEntityType.Street) {
            return entryType === 'Text' ? 'streetName' : 'streetId';
        }
        return getFieldForEntityType(entityTypeId);
    };

    const getChildEntitiesFromRules = (parentEntityTypeId: number): number[] => {
        if (!sortedAddressRules.length) return [];
        return sortedAddressRules
            .filter((rule: any) => rule.parentFieldType === parentEntityTypeId)
            .map((rule: any) => getFieldType(rule));
    };

    const getAllChildEntitiesFromRules = (parentEntityTypeId: number): number[] => {
        const directChildren = getChildEntitiesFromRules(parentEntityTypeId);
        const allChildren = [...directChildren];
        directChildren.forEach((childId) => {
            allChildren.push(...getAllChildEntitiesFromRules(childId));
        });
        return allChildren;
    };

    const getApiMethodForEntityType = (entityTypeId: number): string => {
        switch (entityTypeId) {
            case AddressEntityType.Region:
                return countryId === countryId_KZ || countryId === countryId_UA ? 'GetDistrict' : 'GetCityList';
            case AddressEntityType.City:
                return countryId === countryId_UA ? '' : 'GetCountyList';
            case AddressEntityType.County:
            case AddressEntityType.Neighborhood:
                return 'GetSubCountyList';
            case AddressEntityType.Street:
                return 'GetStreet';
            case AddressEntityType.District:
                return 'GetCityList';
            default:
                return '';
        }
    };

    const loadDataForNextLevel = async (entityTypeId: number, parentValue: any, apiMethod: string) => {
        try {
            switch (apiMethod) {
                case 'GetRegion':
                    await GetRegion(parentValue);
                    break;
                case 'GetCityList':
                    await GetCityList(parentValue);
                    break;
                case 'GetCountyList':
                    await GetCountyList(parentValue);
                    break;
                case 'GetSubCountyList':
                    await GetSubCountyList(parentValue);
                    break;
                case 'GetStreet':
                    await GetStreet(parentValue);
                    break;
                case 'GetDistrict':
                    await GetDistrict(parentValue);
                    break;
                default:
                    return;
            }
        } catch (error) {
            console.error(`Error loading data with ${apiMethod}:`, error);
            setAddressEntityTypeData((prev) => ({
                ...prev,
                [entityTypeId]: [],
            }));
        }
    };

    const resetAndLoadData = (entityTypesToReset: number[], parentValue: any, apiMethod: string) => {
        const allEntitiesToReset = entityTypesToReset.reduce((acc, entityType) => {
            acc.push(entityType);
            acc.push(...getAllChildEntitiesFromRules(entityType));
            return acc;
        }, [] as number[]);

        const uniqueEntitiesToReset = Array.from(new Set(allEntitiesToReset));

        setCustomerAddress((prevAddress: any) => {
            const updatedAddress = { ...prevAddress };
            uniqueEntitiesToReset.forEach((entityType) => {
                const fieldToReset = getFieldForEntityType(entityType);
                if (fieldToReset) {
                    updatedAddress[fieldToReset] = '';
                }
            });
            return updatedAddress;
        });

        setAddressEntityTypeData((prev) => {
            const updated = { ...prev };
            uniqueEntitiesToReset.forEach((entityType) => {
                updated[entityType] = [];
            });
            return updated;
        });

        if (parentValue && apiMethod) {
            entityTypesToReset.forEach((entityTypeId) => {
                loadDataForNextLevel(entityTypeId, parentValue, apiMethod);
            });
        }
    };

    const handleChangeWithRule = (rule: any, value: any, entryType?: string) => {
        const entityTypeId = getFieldType(rule);
        const field = getFieldForEntityTypeWithEntry(entityTypeId, entryType);
        if (field) {
            setCustomerAddress((prevAddress: any) => ({
                ...prevAddress,
                [field]: value,
            }));
        }

        const childEntities = getChildEntitiesFromRules(entityTypeId);
        if (childEntities.length > 0) {
            resetAndLoadData(childEntities, value, getApiMethodForEntityType(entityTypeId));
        }

        if (lastSelectionRule && getFieldType(lastSelectionRule) === entityTypeId) {
            setSelectedDeliveryLocation(null);
            setCustomerAddress((prevAddress: any) => ({
                ...prevAddress,
                selectedDeliveryLocationId: null,
                externalReferanceCode: '',
                externalAddressDefinition: '',
            }));
        }
    };

    const createFormElement = (rule: any, data: Record<number, any>) => {
        const fieldType = getFieldType(rule);
        const entryType = AddressEntryTypeId[getEntryTypeId(rule)];
        const value = getValueForEntityType(fieldType, entryType);
        const label = getLabel(rule);

        switch (entryType) {
            case 'Text':
                return (
                    <FormControl fullWidth variant="outlined">
                        <TextField
                            className={caipEntityTextfield(fieldType)}
                            label={label}
                            variant="outlined"
                            fullWidth
                            value={value}
                            onChange={(e: any) => handleChangeWithRule(rule, e.target.value, entryType)}
                        />
                    </FormControl>
                );
            case 'Selection':
            case 'AllInOneSelection':
                return (
                    <Autocomplete
                        className={caipEntityAutocomplete(fieldType)}
                        fullWidth
                        options={data[fieldType] || []}
                        getOptionLabel={(option: any) => option?.text || ''}
                        isOptionEqualToValue={(option: any, optionValue: any) => option?.value === optionValue?.value}
                        value={(data[fieldType] || []).find((x: any) => x.value === value) || null}
                        onChange={(_, newValue: any) => {
                            handleChangeWithRule(rule, newValue?.value ?? '', entryType);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                variant="outlined"
                                className={caipEntityAutocomplete(fieldType)}
                            />
                        )}
                    />
                );
            case 'Hidden':
            case 'Hybrid':
                return <></>;
            default:
                return <></>;
        }
    };

    const loadAddressRuleOptions = async () => {
        try {
            setIsAllDataLoaded(false);
            setAddressEntityTypeData({});

            if (sortedAddressRules.length > 0) {
                const servicePromises: Promise<any>[] = [];
                sortedAddressRules.forEach((rule: any) => {
                    const fieldType = getFieldType(rule);
                    const entryType = AddressEntryTypeId[getEntryTypeId(rule)];

                    if (entryType === 'Selection' || entryType === 'AllInOneSelection') {
                        if (fieldType === AddressEntityType.PostalCode) {
                            servicePromises.push(GetPostalCodeList());
                        } else if (fieldType === AddressEntityType.City) {
                            servicePromises.push(GetCityList());
                        } else if (fieldType === AddressEntityType.County) {
                            servicePromises.push(GetCountyList());
                        } else if (fieldType === AddressEntityType.Neighborhood) {
                            servicePromises.push(GetSubCountyList());
                        } else if (fieldType === AddressEntityType.Region) {
                            servicePromises.push(GetRegion());
                        } else if (fieldType === AddressEntityType.Street) {
                            servicePromises.push(GetStreet());
                        } else if (fieldType === AddressEntityType.District) {
                            servicePromises.push(GetDistrict());
                        }
                    }
                });

                if (servicePromises.length > 0) {
                    await Promise.all(servicePromises);
                }
            }

            setIsAllDataLoaded(true);
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
            setIsAllDataLoaded(false);
        }
    };

    const loadDeliveryLocationOptions = async () => {
        if (!showDeliveryLocationSelect) {
            setDeliveryLocationOptions([]);
            setSelectedDeliveryLocation(null);
            return;
        }

        setDeliveryLocationLoading(true);
        try {
            if (Number(deliveryMethodId) === DeliveryMethodEnum.PickPoint) {
                if (!lastSelectionAddressEntityId) {
                    setDeliveryLocationOptions([]);
                    setSelectedDeliveryLocation(null);
                    return;
                }

                const res: any = await CargoManagementQuery.GetPickPoints({
                    AddressEntityId: lastSelectionAddressEntityId,
                });
                const parseData =
                    res?.data?.resultList?.map((item: any) => {
                        const referenceCode = (item.referenceCode ?? '').toString().trim();
                        return {
                            value: item.id,
                            text: `${item.name}${referenceCode ? ` (${referenceCode})` : ''}`,
                            referenceCode,
                            raw: item,
                        };
                    }) ?? [];
                setDeliveryLocationOptions(parseData);
            } else if (Number(deliveryMethodId) === DeliveryMethodEnum.StorePickup) {
                const res: any = await OmnichannelQuery.GetStore({
                    CityId: customerAddress?.cityId ?? null,
                    CountyId:
                        customerAddress?.countyId != null && customerAddress?.countyId !== ''
                            ? String(customerAddress.countyId)
                            : '',
                });
                const parseData =
                    (res?.data ?? []).map((item: any) => {
                        const referenceCode = (item.storeCode ?? '').toString().trim();
                        return {
                            value: item.storeId,
                            text: `${item.storeName}${referenceCode ? ` (${referenceCode})` : ''}`,
                            referenceCode,
                            raw: item,
                        };
                    }) ?? [];
                setDeliveryLocationOptions(parseData);
            }
        } catch (error: any) {
            setDeliveryLocationOptions([]);
            Swal.fire('Error', error.toString(), 'error');
        } finally {
            setDeliveryLocationLoading(false);
        }
    };

    const handleDeliveryLocationChange = (newValue: any) => {
        setSelectedDeliveryLocation(newValue);
        const externalAddressDefinition =
            newValue?.raw?.name ?? newValue?.raw?.storeName ?? newValue?.text ?? '';

        const isPickPointOrStorePickup =
            Number(deliveryMethodId) === DeliveryMethodEnum.PickPoint ||
            Number(deliveryMethodId) === DeliveryMethodEnum.StorePickup;

        // StorePickup: raw.address (string)
        // PickPoint: raw.address.fullAddress
        const resolvedAddress = isPickPointOrStorePickup
            ? (newValue?.raw?.address?.fullAddress ??
              (typeof newValue?.raw?.address === 'string' ? newValue.raw.address.trim() : '') ??
              '')
            : null;

        setCustomerAddress((prevAddress: any) => ({
            ...prevAddress,
            selectedDeliveryLocationId: newValue?.value ?? null,
            externalReferanceCode: newValue?.referenceCode ?? '',
            externalAddressDefinition,
            ...(isPickPointOrStorePickup
                ? { address: resolvedAddress || prevAddress.address }
                : {}),
        }));
    };

    useEffect(() => {
        if (sortedAddressRules.length > 0) {
            let hasChanges = false;
            const updatedAddress = { ...customerAddress };

            sortedAddressRules.forEach((rule: any) => {
                const entryType = AddressEntryTypeId[getEntryTypeId(rule)];
                if (entryType === 'Hidden') {
                    const fieldType = getFieldType(rule);
                    const fieldName = getFieldForEntityTypeWithEntry(fieldType, entryType);
                    
                    if (fieldName && updatedAddress[fieldName] !== null) {
                        updatedAddress[fieldName] = null;
                        hasChanges = true;
                    }
                }
            });

            if (hasChanges) {
                setCustomerAddress(updatedAddress);
            }
        }
    }, [sortedAddressRules]);

    useEffect(() => {
        loadAddressRuleOptions();
    }, [sortedAddressRules]);

    useEffect(() => {
        loadDeliveryLocationOptions();
    }, [
        deliveryMethodId,
        lastSelectionAddressEntityId,
        showDeliveryLocationSelect,
        customerAddress?.cityId,
        customerAddress?.countyId,
    ]);

    useEffect(() => {
        if (!showDeliveryLocationSelect || !deliveryLocationOptions.length) {
            return;
        }
        const existingCode = (customerAddress?.externalReferanceCode ?? '').toString().trim();
        if (!existingCode) {
            return;
        }
        const matched = deliveryLocationOptions.find((item: any) => {
            const referenceCode = (item.referenceCode ?? '').toString().trim();
            const optionId = (item.value ?? '').toString().trim();
            return referenceCode === existingCode || optionId === existingCode;
        });
        if (matched) {
            setSelectedDeliveryLocation(matched);
        }
    }, [deliveryLocationOptions, customerAddress?.externalReferanceCode, showDeliveryLocationSelect]);

    const deliveryLocationLabel =
        Number(deliveryMethodId) === DeliveryMethodEnum.PickPoint
            ? t('addressDetail.pickPointSelect')
            : t('addressDetail.storeSelect');

    return (
        <Grid
            className={CAIP.entityFieldsContainer}
            sx={{ marginTop: 0.5 }}
            size={{
                xs: 12,
                md: 12,
                lg: 12,
                xl: 12,
            }}
        >
            {isAllDataLoaded && Object.keys(addressEntityTypeData).length === selectionRulesCount
                ? sortedAddressRules.map((rule: any, index: number) => {
                      const fieldType = getFieldType(rule);
                      return (
                          <div
                              key={`mobile-address-rule-${fieldType}-${index}`}
                              className={caipCls(CAIP.entityFieldRow, caipEntityFieldModifier(fieldType))}
                              style={{ marginBottom: '16px' }}
                          >
                              {createFormElement(rule, addressEntityTypeData)}
                          </div>
                      );
                  })
                : sortedAddressRules.map((rule: any, index: number) => (
                      <Box
                          key={`loading-mobile-address-rule-${getFieldType(rule) || index}`}
                          className={CAIP.entityFieldLoading}
                          sx={{
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: '16px',
                              padding: '12px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              backgroundColor: '#f9f9f9',
                          }}
                      >
                          <LinearProgress sx={{ width: '100%', height: '8px' }} />
                      </Box>
                  ))}

            {showDeliveryLocationSelect && isAllDataLoaded && (
                <div className={CAIP.entityFieldRow} style={{ marginBottom: '16px' }}>
                    {deliveryLocationLoading ? (
                        <Box
                            className={CAIP.entityFieldLoading}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                backgroundColor: '#f9f9f9',
                            }}
                        >
                            <LinearProgress sx={{ width: '100%', height: '8px' }} />
                        </Box>
                    ) : (
                        <Autocomplete
                            className={CAIP.inputTextfieldAddressDefinition}
                            fullWidth
                            options={deliveryLocationOptions}
                            getOptionLabel={(option: any) => option?.text || ''}
                            isOptionEqualToValue={(option: any, optionValue: any) =>
                                option?.value === optionValue?.value
                            }
                            value={selectedDeliveryLocation}
                            onChange={(_, newValue: any) => handleDeliveryLocationChange(newValue)}
                            noOptionsText={t('addressDetail.deliveryLocationEmpty')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={deliveryLocationLabel}
                                    variant="outlined"
                                    placeholder={t('addressDetail.deliveryLocationPlaceholder')}
                                />
                            )}
                        />
                    )}
                </div>
            )}
        </Grid>
    );
};
